import React, { useEffect, useState } from 'react'
import AddEditForm from '../../../sharedComponents/AddEditForm'
import { PencilIcon, DocumentPlusIcon } from '@heroicons/react/24/solid'
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import useManageAgreementTypes from './useManageAgreementTypes';

const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

const emptyAgreement = {
  id: '',
  module: '',
  description: '',
  version: '',
  activeDate: '',
  isActive: true,
  titleTh: '',
  titleEn: '',
  contentTh: '',
  contentEn: ''
};

const AgreementTypeSetting = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [agreement, setAgreement] = useState(emptyAgreement);
  // false = edit the current version in place (typo fix, nobody re-accepts);
  // true = publish it as the next version (every customer must accept again).
  const [isPublishing, setIsPublishing] = useState(false);

  const {
    agreementTypes,
    editAgreementType,
    publishAgreementVersion,
    refreshAgreementTypes
  } = useManageAgreementTypes();

  // module is display-only (referenced by backend code) — not in the edit form.
  // version is set by the backend on publish, so it is not editable either.
  const fields = [
    { title: 'Title (TH)', name: 'titleTh', type: 'text' },
    { title: 'Title (EN)', name: 'titleEn', type: 'text' },
    { title: 'Content (TH) — Markdown: "## " heading, "- " list, "1. " numbered list, *text* red bold (small grey note inside a heading), **bold**; blank line = new paragraph', name: 'contentTh', type: 'textarea' },
    { title: 'Content (EN) — same Markdown as TH', name: 'contentEn', type: 'textarea' },
    { title: 'Internal Description', name: 'description', type: 'textarea' },
    { title: 'Active Date', name: 'activeDate', type: 'date' },
    { title: 'Is Active', name: 'isActive', type: 'boolean' },
  ];
  // A published version is always the active one.
  const items = isPublishing ? fields.filter(f => f.name !== 'isActive') : fields;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAgreement({
      ...agreement,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleDateChange = (date, name) => {
    setAgreement({ ...agreement, [name]: date });
  };

  const openForm = (id, publish = false) => {
    const toEdit = agreementTypes.find(a => a.id === id);
    if (toEdit) {
      setIsPublishing(publish);
      setAgreement({
        id: toEdit.id,
        module: toEdit.module || '',
        description: toEdit.description || '',
        version: toEdit.version || '',
        // A published version starts today unless the admin picks another date.
        activeDate: publish ? new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' }) : (toEdit.activeDate || ''),
        isActive: toEdit.isActive !== undefined ? toEdit.isActive : true,
        titleTh: toEdit.titleTh || '',
        titleEn: toEdit.titleEn || '',
        contentTh: toEdit.contentTh || '',
        contentEn: toEdit.contentEn || ''
      });
      setIsFormOpen(true);
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  const handleUpdateAgreement = async () => {
    try {
      const { id, module, version, ...editable } = agreement;
      await editAgreementType(id, editable);
      toast.success('Agreement updated successfully!');
      closeForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handlePublishAgreement = async () => {
    const nextVersion = agreement.version + 1;
    if (!window.confirm(`เผยแพร่ ${agreement.module} เวอร์ชัน ${nextVersion}?\nลูกค้าทุกคนต้องกดยอมรับข้อตกลงใหม่อีกครั้ง`)) return;
    try {
      const { id, module, version, isActive, ...publishable } = agreement;
      await publishAgreementVersion(id, publishable);
      toast.success(`Published version ${nextVersion}`);
      closeForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    refreshAgreementTypes();
  }, []);

  return (
    <div>
      <Modal
        isOpen={isFormOpen}
        onRequestClose={closeForm}
        style={customStyles}
        ariaHideApp={false}
      >
        <p className="text-center text-sm text-gray-500">
          Module: <span className="font-mono">{agreement.module}</span> (not editable)
        </p>
        <p className="text-center text-sm text-gray-500">
          {isPublishing
            ? 'Saves as a new version. Every customer must accept it again.'
            : `Edits version ${agreement.version} in place (typo fixes). Customers who accepted stay accepted.`}
        </p>
        <AddEditForm
          items={items}
          name={isPublishing ? `Publish Version ${agreement.version + 1}` : 'Update Agreement'}
          data={agreement}
          mode="edit"
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleDateChange={handleDateChange}
          handleUpdate={isPublishing ? handlePublishAgreement : handleUpdateAgreement}
          submitLabel={isPublishing ? 'Publish' : undefined}
          stateValue={agreement}
        />
      </Modal>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mt-4 flex flex-col">
          <div className='flex justify-between items-center mb-4'>
            <h2 className='text-5xl'>Agreement Setting</h2>
          </div>
          <div className="shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg overflow-hidden">
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-0">
                  <tr>
                    <th scope="col" className="py-4 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Module
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Title (TH / EN)
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Content set?
                    </th>
                    <th scope="col" className="px-3 py-4 text-right text-sm font-semibold text-gray-900">
                      Version
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Active Date
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Active
                    </th>
                    <th scope="col" className="relative py-4 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {agreementTypes.map((a) => {
                    const { id, module, titleTh, titleEn, contentTh, contentEn, version, activeDate, isActive } = a;
                    const hasContent = !!(contentTh || contentEn);
                    return (
                      <tr key={id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-mono text-gray-900 sm:pl-6">
                          {module}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {titleTh || <span className="italic text-gray-300">—</span>} / {titleEn || <span className="italic text-gray-300">—</span>}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {hasContent ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Yes</span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Empty</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                          {version}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {activeDate}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {isActive ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Inactive</span>
                          )}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button type="button" onClick={() => openForm(id)} title="Edit (typo fix)" aria-label="Edit (typo fix)" className="p-1">
                            <PencilIcon className='inline-block w-6 h-6 fill-green-600' />
                          </button>
                          <button type="button" onClick={() => openForm(id, true)} title="Publish new version" aria-label="Publish new version" className="p-1 ml-2">
                            <DocumentPlusIcon className='inline-block w-6 h-6 fill-blue-600' />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementTypeSetting;
